import { useRef } from 'react';
import { PencilSimple, Trash } from '@phosphor-icons/react';
import {
  DrawerTooltipContainer,
  DrawerTooltipContainerRef,
} from 'src/v2/commonComponents/DrawerTooltipContainer';
import { Button } from 'src/v2/commonComponents/Button';
import { SVG_SIZE_M } from 'src/constants';
import {
  useGetUser,
  useQueryParams,
  useRightSidePanelActions,
  useToast,
  useErrorToast,
  useVisible,
  useEditorTitleRename,
} from 'src/hooks';
import { CommonDeleteModal } from 'src/components/CommonDeleteModal';
import { useDeleteEditorThreadMutation } from 'src/store/services';
import { RenameFileModal } from '../RenameFileModal';
import styles from './EditorMenuControl.module.scss';
import { ThreeDotsToggle } from 'src/v2/commonComponents/ThreeDotsToggle';
import { useChatHistory } from 'src/hooks';

const tooltipHeaderAnchorId = 'editor-menu-header-tooltip-anchor';
const tooltipLeftMenuAnchorId = 'editor-left-menu-tooltip-anchor';

interface EditorMenuControlProps {
  align?: 'bottom' | 'bottom-end';
  threadId: string;
  threadTitle?: string | null;
  placement: 'header' | 'left-menu';
}

export const EditorMenuControl = ({
  align = 'bottom-end',
  threadId,
  threadTitle,
  placement,
}: EditorMenuControlProps) => {
  const { isNinjaRetired } = useChatHistory();
  const drawerRef = useRef<DrawerTooltipContainerRef>(null);

  const { user_id } = useGetUser();
  const { removeSearchParam } = useQueryParams();
  const { toggleRightSidePanelExpanded, toggleRightSidePanelFullWidth } =
    useRightSidePanelActions();
  const { onTitleRename, isRenameLoading } = useEditorTitleRename();

  const [deleteEditorThread, { isLoading: isDeleteLoading }] =
    useDeleteEditorThreadMutation();

  const {
    isVisible: isDeleteModalVisible,
    onVisibilitySet: onDeleteModalVisibilitySet,
    onVisibilityRemove: onDeleteModalVisibilityRemove,
  } = useVisible();

  const {
    isVisible: isRenameModalVisible,
    onVisibilitySet: onRenameModalVisibilitySet,
    onVisibilityRemove: onRenameModalVisibilityRemove,
  } = useVisible();

  const { onShowToast } = useToast();
  const { onShowErrorToast } = useErrorToast();

  const handleDeleteClick = () => {
    drawerRef.current?.onVisibilityToggle();
    onDeleteModalVisibilitySet();
  };

  const handleRenameClick = () => {
    drawerRef.current?.onVisibilityToggle();
    onRenameModalVisibilitySet();
  };

  const handleSubmit = async () => {
    removeSearchParam('editor', { replace: true });

    try {
      await deleteEditorThread({ user_id, thread_id: threadId }).unwrap();
      toggleRightSidePanelFullWidth(false);
      toggleRightSidePanelExpanded(false);
      onShowToast('File deleted', 'success');
    } catch (error) {
      onShowErrorToast();
    }
  };

  const handleRenameSubmit = async (newTitle: string) => {
    onRenameModalVisibilityRemove();

    await onTitleRename({
      newTitle,
    });
  };

  return (
    <>
      {!isNinjaRetired && (
        <RenameFileModal
          defaultValue={threadTitle || ''}
          isOpen={isRenameModalVisible}
          onClose={onRenameModalVisibilityRemove}
          onSubmit={handleRenameSubmit}
          isLoading={isRenameLoading}
        />
      )}

      <CommonDeleteModal
        isOpen={isDeleteModalVisible}
        onClose={onDeleteModalVisibilityRemove}
        onSubmit={handleSubmit}
        isLoading={isDeleteLoading}
        title="Delete file"
        target={threadTitle || 'Document'}
      />

      <DrawerTooltipContainer
        ref={drawerRef}
        id={
          placement === 'left-menu'
            ? tooltipLeftMenuAnchorId
            : tooltipHeaderAnchorId
        }
        place={align}
        offset={8}
        classNameTooltip={styles.tooltip}
        onlyTooltip
        ToggleButton={ThreeDotsToggle}
      >
        <div className={styles.container}>
          {/* TODO(olha): WIP Editor move to the separate component */}
          <Button
            disabled={isDeleteLoading}
            color="transparent"
            onClick={handleDeleteClick}
          >
            <Trash size={SVG_SIZE_M} />

            <span>Delete file</span>
          </Button>

          {!isNinjaRetired && (
            <Button
              disabled={isRenameLoading}
              color="transparent"
              onClick={handleRenameClick}
            >
              <PencilSimple size={SVG_SIZE_M} color="currentColor" />

              <span>Rename</span>
            </Button>
          )}
        </div>
      </DrawerTooltipContainer>
    </>
  );
};
